import { useState, useMemo, Fragment } from 'react';
import { format } from 'date-fns';
import { fmtDate } from '@/lib/utils';
import { ArrowUpDown, ArrowUp, ArrowDown, Clock, TrendingDown, TrendingUp, Minus } from 'lucide-react';

const SORTABLE = {
  product_name: (r) => r.product_name.toLowerCase(),
  period_purchased: (r) => r.period_purchased,
  period_sold: (r) => r.period_sold,
  last_sold_date: (r) => r.last_sold_date || ''
};

export default function SupplierStockTable({ products, loading, monthLabels, compareWindows = [], orderQty, onOrderQtyChange, onSetStock }) {
  const [sortBy, setSortBy] = useState('period_sold');
  const [sortOrder, setSortOrder] = useState('desc');

  const sorted = useMemo(() => {
    const key = SORTABLE[sortBy] || SORTABLE.period_sold;
    const arr = [...products].sort((a, b) => {
      const va = key(a); const vb = key(b);
      if (va < vb) return sortOrder === 'asc' ? -1 : 1;
      if (va > vb) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [products, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const tm = monthLabels?.this_month || 'This Month';
  const lm = monthLabels?.last_month || 'Last Month';
  const colCount = 13 + compareWindows.length * 2;

  const TrendIcon = ({ cur, prev, barcode, wkey, vs }) => {
    if (!cur && !prev) return null;
    const t = vs ? ` (${vs})` : '';
    if (cur > prev) return <TrendingUp data-testid={`trend-up-w${wkey}-${barcode}`} className="h-3 w-3 text-emerald-600 inline ml-1" title={`Rising — sold ${cur} vs ${prev} in the previous period${t}`} />;
    if (cur < prev) return <TrendingDown data-testid={`trend-down-w${wkey}-${barcode}`} className="h-3 w-3 text-red-500 inline ml-1" title={`Falling — sold ${cur} vs ${prev} in the previous period${t}`} />;
    return <Minus data-testid={`trend-steady-w${wkey}-${barcode}`} className="h-3 w-3 text-zinc-400 inline ml-1" title={`Steady — sold ${cur} vs ${prev} in the previous period${t}`} />;
  };

  return (
    <div className="border border-zinc-200 bg-white">
      <div data-testid="supplier-stock-table" className="overflow-auto max-h-[calc(100vh-120px)]">
        <table className="w-full control-table sticky-header-table">
          <thead>
            <tr className="bg-zinc-50">
              <th colSpan={3} className="border-b border-zinc-200"></th>
              <th className="text-center border-b border-l border-zinc-200 text-zinc-600">Stock</th>
              <th colSpan={2} className="text-center border-b border-l border-zinc-200 text-emerald-700">Selected Period</th>
              {compareWindows.map((w) => (
                <th key={w.key} colSpan={2} data-testid={`window-group-${w.key}`} className="text-center border-b border-l border-zinc-200 text-amber-700">
                  <div>{w.label}</div>
                  <div className="text-[9px] font-medium normal-case tracking-normal text-zinc-400">{w.dates}</div>
                </th>
              ))}
              <th colSpan={2} className="text-center border-b border-l border-zinc-200">{tm}</th>
              <th colSpan={2} className="text-center border-b border-l border-zinc-200 text-zinc-400">{lm}</th>
              <th colSpan={2} className="border-b border-l border-zinc-200"></th>
              <th className="border-b border-l border-zinc-200 text-center text-emerald-700">Order</th>
            </tr>
            <tr>
              <th className="cursor-pointer select-none" onClick={() => handleSort('product_name')}>
                <span className="flex items-center gap-1.5">Product <SortIcon field="product_name" /></span>
              </th>
              <th>Barcode</th>
              <th className="text-right">MRP</th>
              <th
                className="text-right border-l border-zinc-100 w-[110px]"
                title="Estimate = purchases − sales over the last 6 months. Confirm a physical count (Set Stock) to track exactly from that point."
              >AAS</th>
              <th className="cursor-pointer select-none text-right border-l border-zinc-100" onClick={() => handleSort('period_purchased')}>
                <span className="flex items-center justify-end gap-1.5">Purchased <SortIcon field="period_purchased" /></span>
              </th>
              <th className="cursor-pointer select-none text-right" onClick={() => handleSort('period_sold')}>
                <span className="flex items-center justify-end gap-1.5">Sold <SortIcon field="period_sold" /></span>
              </th>
              {compareWindows.map((w) => (
                <Fragment key={`wh-${w.key}`}>
                  <th className="text-right border-l border-zinc-100">Purch</th>
                  <th className="text-right">Sold</th>
                </Fragment>
              ))}
              <th className="text-right border-l border-zinc-100">Purch</th>
              <th className="text-right">Sold</th>
              <th className="text-right border-l border-zinc-100">Purch</th>
              <th className="text-right">Sold</th>
              <th className="cursor-pointer select-none text-right border-l border-zinc-100" onClick={() => handleSort('last_sold_date')}>
                <span className="flex items-center justify-end gap-1.5">Last Sold <SortIcon field="last_sold_date" /></span>
              </th>
              <th className="text-right">Last Purchased</th>
              <th className="text-center border-l border-zinc-100 w-[90px]">Order Qty</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-6 w-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-zinc-400">Loading supplier data...</span>
                  </div>
                </td>
              </tr>
            ) : !sorted.length ? (
              <tr>
                <td colSpan={colCount} className="text-center py-16">
                  <p className="text-sm text-zinc-500">No active products found for this supplier</p>
                </td>
              </tr>
            ) : (
              sorted.map((item, idx) => {
                const isNew = item.indicators?.includes('new_product');
                const isSlow = item.indicators?.includes('slow_product');
                const notSelling = item.indicators?.includes('not_selling');
                return (
                  <tr key={item.barcode} className="animate-fade-in" style={{ animationDelay: `${Math.min(idx, 20) * 12}ms` }}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-zinc-900">{item.product_name}</span>
                        {isNew && (
                          <span data-testid={`sv-badge-new-${item.barcode}`} className="bg-sky-100 text-sky-700 border border-sky-200 text-[9px] font-bold px-1 py-0 uppercase tracking-wider shrink-0">New</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {notSelling && (
                          <span data-testid={`sv-badge-notselling-${item.barcode}`} className="flex items-center gap-1 text-[10px] text-red-600 bg-red-50 border border-red-200 px-1.5 py-0 font-medium">
                            <TrendingDown className="h-2.5 w-2.5" /> Bought, not selling
                          </span>
                        )}
                        {isSlow && (
                          <span className="flex items-center gap-1 text-[10px] text-orange-600 bg-orange-50 border border-orange-200 px-1.5 py-0 font-medium">
                            <Clock className="h-2.5 w-2.5" /> Slow/Dead product
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="num-cell text-left text-zinc-500">{item.barcode || '-'}</td>
                    <td className="num-cell text-zinc-500">{item.mrp > 0 ? item.mrp : '-'}</td>
                    <td className="border-l border-zinc-100">
                      <div className="flex flex-col items-end gap-1 py-0.5">
                        <span
                          data-testid={`aas-value-${item.barcode}`}
                          className={`font-mono text-sm font-bold ${item.stock?.value < 0 ? 'text-red-600' : 'text-zinc-900'}`}
                        >
                          {Number(item.stock?.value ?? 0).toLocaleString()}
                        </span>
                        {item.stock?.status === 'confirmed' ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] text-emerald-600 whitespace-nowrap">
                              Anchored {item.stock.anchored_at ? format(new Date(item.stock.anchored_at), 'dd-MM-yyyy') : ''}
                            </span>
                            <button
                              data-testid={`stock-action-${item.barcode}`}
                              onClick={() => onSetStock?.(item)}
                              className="text-[10px] font-semibold px-1.5 py-0.5 border border-zinc-300 text-zinc-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors"
                            >
                              Edit
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[8px] font-bold text-zinc-400 border border-zinc-200 bg-zinc-50 px-1 uppercase tracking-wider">Est.</span>
                            <button
                              data-testid={`stock-action-${item.barcode}`}
                              onClick={() => onSetStock?.(item)}
                              className="text-[10px] font-semibold px-1.5 py-0.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors whitespace-nowrap"
                            >
                              Set Stock
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="num-cell border-l border-zinc-100 text-sky-700 font-semibold">
                      {item.period_purchased > 0 ? item.period_purchased.toLocaleString() : <span className="text-zinc-300">0</span>}
                    </td>
                    <td className="num-cell text-emerald-700 font-semibold">
                      {item.period_sold > 0 ? item.period_sold.toLocaleString() : <span className="text-zinc-300">0</span>}
                    </td>
                    {compareWindows.map((w) => (
                      <Fragment key={`wc-${w.key}`}>
                        <td data-testid={`w${w.key}-purch-${item.barcode}`} className="num-cell border-l border-zinc-100 text-amber-700">
                          {item[`w${w.key}_purchased`] > 0 ? item[`w${w.key}_purchased`].toLocaleString() : <span className="text-zinc-300">0</span>}
                        </td>
                        <td data-testid={`w${w.key}-sold-${item.barcode}`} className="num-cell text-amber-800 font-medium whitespace-nowrap">
                          {item[`w${w.key}_sold`] > 0 ? item[`w${w.key}_sold`].toLocaleString() : <span className="text-zinc-300">0</span>}
                          <TrendIcon
                            cur={item[`w${w.key}_sold`] || 0}
                            prev={item[`w${w.key}_prev_sold`] || 0}
                            barcode={item.barcode}
                            wkey={w.key}
                            vs={w.prev}
                          />
                        </td>
                      </Fragment>
                    ))}
                    <td className="num-cell border-l border-zinc-100 text-zinc-700">{item.this_month_purchased > 0 ? item.this_month_purchased.toLocaleString() : <span className="text-zinc-300">0</span>}</td>
                    <td className="num-cell text-zinc-700">{item.this_month_sold > 0 ? item.this_month_sold.toLocaleString() : <span className="text-zinc-300">0</span>}</td>
                    <td className="num-cell border-l border-zinc-100 text-zinc-400">{item.last_month_purchased > 0 ? item.last_month_purchased.toLocaleString() : <span className="text-zinc-300">0</span>}</td>
                    <td className="num-cell text-zinc-400">{item.last_month_sold > 0 ? item.last_month_sold.toLocaleString() : <span className="text-zinc-300">0</span>}</td>
                    <td className="num-cell text-zinc-500 border-l border-zinc-100 text-right">{fmtDate(item.last_sold_date) || '-'}</td>
                    <td className="num-cell text-zinc-500 text-right">{fmtDate(item.last_purchased_date) || '-'}</td>
                    <td className="border-l border-zinc-100 text-center">
                      <input
                        data-testid={`order-qty-${item.barcode}`}
                        type="number"
                        min="0"
                        placeholder="0"
                        value={orderQty?.[item.barcode] ?? ''}
                        onChange={(e) => onOrderQtyChange?.(item.barcode, e.target.value)}
                        className={`w-[72px] h-8 border rounded-none px-2 text-right font-mono text-sm focus:outline-none transition-colors ${
                          Number(orderQty?.[item.barcode]) > 0
                            ? 'border-emerald-500 bg-emerald-50/50 text-emerald-800 font-semibold'
                            : 'border-zinc-300 focus:border-emerald-600'
                        }`}
                      />
                      {item.suggested_qty > 0 && (
                        <button
                          data-testid={`suggested-qty-${item.barcode}`}
                          onClick={() => onOrderQtyChange?.(item.barcode, String(item.suggested_qty))}
                          className="block mx-auto mt-0.5 text-[10px] font-semibold text-amber-700 hover:text-amber-900 hover:underline whitespace-nowrap"
                          title={`Suggested from this product's weekly sales rhythm${item.stock?.status === 'confirmed' ? ', minus confirmed stock' : ''}. Click to fill.`}
                        >
                          Sug: {item.suggested_qty}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
